import { AppError, type ErrorKind } from '@/lib/errors/appError'

export type ErrorTier = 'component' | 'feature' | 'root'

interface ErrorCopy {
  title: string
  description: string
}

const COPY: Record<ErrorKind, Record<ErrorTier, ErrorCopy>> = {
  network: {
    component: { title: "This couldn't load", description: "We couldn't reach Nivex just now." },
    feature: {
      title: 'We couldn’t reach Nivex',
      description: 'Check your connection and try again.',
    },
    root: {
      title: 'We couldn’t reach Nivex',
      description: 'Check your connection and try again in a moment.',
    },
  },
  timeout: {
    component: { title: 'This is taking too long', description: 'The request timed out.' },
    feature: { title: 'That took longer than expected', description: 'Please try again.' },
    root: { title: 'That took longer than expected', description: 'Please try again.' },
  },
  unauthorized: {
    component: {
      title: "You've been signed out",
      description: 'Please sign in again to continue.',
    },
    feature: { title: "You've been signed out", description: 'Please sign in again to continue.' },
    root: { title: "You've been signed out", description: 'Please sign in again to continue.' },
  },
  forbidden: {
    component: {
      title: "You don't have access",
      description: "Contact your advisor if this doesn't seem right.",
    },
    feature: {
      title: "You don't have access to this",
      description: "Contact your advisor if this doesn't seem right.",
    },
    root: {
      title: "You don't have access to this",
      description: "Contact your advisor if this doesn't seem right.",
    },
  },
  'not-found': {
    component: {
      title: "We couldn't find that",
      description: 'It may have moved or been removed.',
    },
    feature: { title: "We couldn't find that", description: 'It may have moved or been removed.' },
    root: {
      title: "We couldn't find that page",
      description: 'It may have moved or been removed.',
    },
  },
  validation: {
    component: {
      title: "That didn't go through",
      description: 'Please check the information and try again.',
    },
    feature: {
      title: "That didn't go through",
      description: 'Please check the information and try again.',
    },
    root: {
      title: "That didn't go through",
      description: 'Please check the information and try again.',
    },
  },
  conflict: {
    component: {
      title: 'Already done',
      description: 'This already exists or was already done.',
    },
    feature: {
      title: 'Already done',
      description: 'This already exists or was already done — refresh to see the latest.',
    },
    root: {
      title: 'Already done',
      description: 'This already exists or was already done — refresh to see the latest.',
    },
  },
  server: {
    component: {
      title: 'Something went wrong',
      description: 'This part of the page ran into a problem.',
    },
    feature: {
      title: 'Something went wrong on our end',
      description: "We're looking into it. Please try again shortly.",
    },
    root: {
      title: 'Something went wrong on our end',
      description: "We're looking into it. Please try again shortly.",
    },
  },
  unknown: {
    component: {
      title: 'Something went wrong',
      description: 'This part of the page ran into a problem.',
    },
    feature: {
      title: 'Something went wrong',
      description: 'Please try again, or go back and try another way.',
    },
    root: {
      title: 'Something went wrong',
      description: 'Please try again, or go back and try another way.',
    },
  },
}

export function getErrorCopy(error: unknown, tier: ErrorTier): ErrorCopy {
  const appError = AppError.fromUnknown(error)
  return COPY[appError.kind][tier]
}
